import React, { useEffect, useState } from 'react'
import './App.css'
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Link, Routes, Route, Navigate, useNavigate } from 'react-router';
import { login, logout } from './redux/slices/userInfoSlice.js';
import PrivateRoute from './components/layout/PrivateRoute.jsx';
import Login from './pages/common/Login.jsx';
import Header from './components/layout/Header.jsx';
import TabNavigation from './components/layout/TabNavigation.jsx';
import Overview from './pages/admin/Overview.jsx';
import Organizer from './pages/orgnanizer/Organizer.jsx';
import Events from './pages/admin/Events.jsx';
import bgDashboard from './assets/images/bgdashboard.jpg'
import Analytics from './pages/common/Analytics.jsx';
import Settings from './pages/admin/Settings.jsx';
import CreateEventForm from './components/forms/CreateEventForm.jsx';
import CreateOrganizerForm from './components/forms/CreateOrganizerForm.jsx';
import OrganizerDashboard from "./pages/orgnanizer/OrganizerDashboard.jsx"
import { useLocation } from "react-router";
import { Toaster } from 'sonner';
import ThemePortalConfig from './pages/common/ThemPortalConfig.jsx';
import EventDataViewer from './pages/common/EventDataViewer.jsx';
import { apiConnecter } from './services/apiConnector.js';
import API_ENDPOINTS from './constant/EndPoints.js';

// --- MAIN APP COMPONENT ---

/*******************************************************************
 *                   APP COMPONENT (MAIN ROUTER)                   *
 *******************************************************************
 * This component handles global state, routing, and conditional   *
 * rendering for the SuperAdmin/Organizer dashboard vs. Analytics. *
 *****************************************************************/

const App = () => {

  /* ****************************************************************
  *                             USE STATES                         *
  ******************************************************************/
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAnalyticsPage = location.pathname.includes("/admin/analytics");
  const userInfo = useSelector((state) => state.userInfo);
  const isLoggedIn = useSelector((state) => state.userInfo?.isLoggedIn);
  const [currentTab, setCurrentTab] = useState("overview");
  const [editUserInfo, setEditUserInfo] = useState(null)
  const [analyticsPage, setAnalyticsPage] = useState(false)



  /* ****************************************************************
  *                            HELPER FUNCTION                        *
  ******************************************************************/

  const onTabChange = (tab) => {
    navigate(`/admin/${tab.toLowerCase()}`)
    setCurrentTab(tab)
  }

  /* ****************************************************************
  *                             EFFECT HOOKS                        *
  ******************************************************************/
  // function to validate token
  const ValidateToken = async () => {
    try {
      const response = await apiConnecter("GET",
        `${API_ENDPOINTS.REACT_APP_VALIDATE_TOKEN_END_POINT}`,
        null,
        { authorization: `Bearer ${userInfo.token}` }
      );
      // Assuming the API structure is response.data.result


    } catch (err) {
      dispatch(logout())
      console.error("Error Validating token:", err?.response?.data?.message ?? err.message);
      // Implement visible error handling here
    } finally {
      // Unset loading state here
    }
  };

  useEffect(() => {
    ValidateToken()
  }, [])

  useEffect(() => {
    const tabs = [
      "/admin/overview",
      "/admin/events",
      "/admin/organizers",
      "/admin/settings",
    ];
    const path = location.pathname.trim().toLowerCase();
    const secondPath = location.pathname.split("/")[2]
    if (tabs.includes(path)) {
      setCurrentTab(secondPath);
      setAnalyticsPage(false)
    }
    else {
      setAnalyticsPage(true)
    }
  }, [location.pathname, currentTab]);

  return (

    <div className="bg-transparent min-h-screen font-sans antialiased custom-scrollbar" >

      {!isAnalyticsPage ? (
        <div className="bg-[var(--color-surface-background)] min-h-screen font-sans antialiased custom-scrollbar" style={{
          backgroundColor: 'var(--primary-bg-color)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>

          {/* Conditionally render Header for all logged-in, non-analytics pages */}
          {
            isLoggedIn &&
            <Header isAnalytics={false}></Header>
          }

          {/* Conditionally render Tab Navigation only for SuperAdmin and if not on Analytics page */}
          {
            userInfo?.user?.role === "superAdmin"
            &&
            <div className='bg-[var(-analytics-card-color)] p-1 rounded-full mt-1'>
              <TabNavigation {...{ currentTab, onTabChange }}></TabNavigation>
            </div>
          }


          <main className="flex justify-center  ">

            {/* *********************************************************
              *                 MAIN APPLICATION ROUTES                 *
              *********************************************************/}

            <Routes>
              {/* login redirect */}
              {isLoggedIn ? (
                userInfo?.user?.role === "superAdmin" ?
                  <>
                    <Route path="/admin/login" element={<Navigate to="/admin/overview" />} />
                    <Route path="*" element={<Navigate to="/admin/overview" />} />
                  </> :
                  <>
                    <Route path="/admin/login" element={<Navigate to="/admin/org" />} />
                    <Route path="*" element={<Navigate to="/admin/org" />} />
                  </>
              ) : (
                <Route path="/admin/login" element={<Login />} />
              )}

              {/* *********************************************************
              *              SUPERADMIN-ONLY PROTECTED ROUTES          *
              *********************************************************/}
              <Route
                path="/admin/overview"
                element={<PrivateRoute role={userInfo?.user?.role} isLoggedIn={isLoggedIn} allowedRoles={["superAdmin"]}><Overview /></PrivateRoute>}
              />
              <Route
                path="/admin/organizers"
                element={
                  <PrivateRoute
                    role={userInfo?.user?.role}
                    isLoggedIn={isLoggedIn}
                    allowedRoles={["superAdmin"]}
                  >
                    <Organizer
                      editUserInfo={editUserInfo}
                      setEditUserInfo={setEditUserInfo}
                    />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/events"
                element={
                  <PrivateRoute
                    role={userInfo?.user?.role}
                    isLoggedIn={isLoggedIn}
                    allowedRoles={["superAdmin"]}
                  >
                    <Events />
                  </PrivateRoute>
                }
              />

              <Route
                path="/admin/settings"
                element={
                  <PrivateRoute
                    role={userInfo?.user?.role}
                    isLoggedIn={isLoggedIn}
                    allowedRoles={["superAdmin"]}
                  >
                    <Settings />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/feedback"
                element={
                  <PrivateRoute
                    role={userInfo?.user?.role}
                    isLoggedIn={isLoggedIn}
                    allowedRoles={["superAdmin", "organizer"]}
                  >
                    <EventDataViewer />
                  </PrivateRoute>
                }

              />
              <Route
                path="/admin/ThemePortalConfig"
                element={
                  <PrivateRoute
                    role={userInfo?.user?.role}
                    isLoggedIn={isLoggedIn}
                    allowedRoles={["superAdmin"]}
                  >
                    <ThemePortalConfig />
                  </PrivateRoute>
                }

              />
              {/**********************************************************
              *                 ORGANIZER-ONLY PROTECTED ROUTE           *
              *********************************************************/}
              <Route
                path="/admin/org"
                element={
                  <PrivateRoute
                    role={userInfo?.user?.role}
                    isLoggedIn={isLoggedIn}
                    allowedRoles={["organizer"]}
                  >
                    <OrganizerDashboard />
                  </PrivateRoute>
                }
              />
            </Routes>

          </main>
        </div>
      ) : (<div className="bg-[var(--color-surface-background)] min-h-screen font-sans antialiased custom-scrollbar">

        {isLoggedIn && isAnalyticsPage && <Header isAnalytics={true} />}

        {/**********************************************************
              *                 ANALYTICS COMMON ROUTE                  *
              *********************************************************/}

        <main className="flex justify-center  bg-[#FFFFFF] ">
          <Routes>
            <Route
              path="/admin/analytics/:id"
              element={
                <PrivateRoute
                  role={userInfo?.user?.role}
                  isLoggedIn={isLoggedIn}
                  allowedRoles={["superAdmin", "organizer"]}
                >
                  <Analytics />
                </PrivateRoute>
              }
            />
          </Routes>
        </main>
      </div>)}
      <Toaster position="top-left" richColors />
    </div>

  );
};

export default App